import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Heart, CreditCard, Wallet, Copy, Check, ExternalLink, QrCode } from 'lucide-react';

const PAYSTACK_DONATE_URL = "https://paystack.com/buy/faire-un-don-isgyxz";
const CRYPTO_WALLET = "TC53RQao1tQqfNDFmVBEXMXJnqZLDmn8zX";
const CRYPTO_QR_CODE = "https://customer-assets.emergentagent.com/job_pan-africa-hub/artifacts/6fqoqp4t_WhatsApp%20Image%202026-02-25%20at%2018.25.25.jpeg";

const DonatePage = () => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');

  const copyToClipboard = () => {
    navigator.clipboard.writeText(CRYPTO_WALLET);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-oar-blue via-oar-blue-dark to-oar-blue overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-oar-orange/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-oar-orange/20 rounded-full mb-8">
            <Heart className="w-10 h-10 text-oar-orange" />
          </div>
          
          <h1 
            data-testid="donate-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {t('donate.title')}
          </h1>
          
          <p 
            data-testid="donate-subtitle"
            className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed"
          >
            {t('donate.subtitle')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-oar-bg">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {/* Description Card */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-12 border-l-4 border-oar-orange">
            <p className="text-oar-text text-lg leading-relaxed">
              {t('donate.description')}
            </p>
          </div>

          {/* Payment Methods Selection */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Card Payment Option */}
            <button
              onClick={() => setPaymentMethod('card')}
              data-testid="payment-method-card"
              className={`p-6 rounded-lg border-2 transition-all duration-300 text-left ${
                paymentMethod === 'card'
                  ? 'border-oar-blue bg-oar-blue-soft shadow-lg'
                  : 'border-oar-border bg-white hover:border-oar-blue/50'
              }`}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  paymentMethod === 'card' ? 'bg-oar-blue' : 'bg-oar-bg'
                }`}>
                  <CreditCard className={`w-6 h-6 ${paymentMethod === 'card' ? 'text-white' : 'text-oar-blue'}`} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-xl text-oar-text">
                    {t('donate.cardPayment')}
                  </h3>
                  <p className="text-sm text-oar-text-secondary">Visa / Mastercard</p>
                </div>
              </div>
              <p className="text-oar-text-secondary text-sm">
                {t('donate.cardDescription')}
              </p>
            </button>

            {/* Crypto Payment Option */}
            <button
              onClick={() => setPaymentMethod('crypto')}
              data-testid="payment-method-crypto"
              className={`p-6 rounded-lg border-2 transition-all duration-300 text-left ${
                paymentMethod === 'crypto'
                  ? 'border-oar-orange bg-oar-orange/5 shadow-lg'
                  : 'border-oar-border bg-white hover:border-oar-orange/50'
              }`}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  paymentMethod === 'crypto' ? 'bg-oar-orange' : 'bg-oar-bg'
                }`}>
                  <Wallet className={`w-6 h-6 ${paymentMethod === 'crypto' ? 'text-white' : 'text-oar-orange'}`} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-xl text-oar-text">
                    {t('donate.cryptoPayment')}
                  </h3>
                  <p className="text-sm text-oar-text-secondary">USDT TRC20</p>
                </div>
              </div>
              <p className="text-oar-text-secondary text-sm">
                {t('donate.cryptoDescription')}
              </p>
            </button>
          </div>

          {/* Payment Details */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {paymentMethod === 'card' ? (
              /* Card Payment Section */
              <div className="p-8 md:p-12 text-center">
                <div className="w-20 h-20 bg-oar-blue/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CreditCard className="w-10 h-10 text-oar-blue" />
                </div>
                
                <h3 className="font-heading text-2xl font-semibold text-oar-text mb-4">
                  {t('donate.cardTitle')}
                </h3>
                
                <p className="text-oar-text-secondary mb-8 max-w-md mx-auto">
                  {t('donate.cardInstructions')}
                </p>
                
                <a
                  href={PAYSTACK_DONATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="donate-card-btn"
                  className="inline-flex items-center gap-3 bg-oar-blue hover:bg-oar-blue-dark text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <CreditCard className="w-5 h-5" />
                  {t('donate.cardButton')}
                  <ExternalLink className="w-4 h-4" />
                </a>
                
                <p className="mt-6 text-sm text-oar-text-secondary flex items-center justify-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  {t('donate.securePayment')}
                </p>
              </div>
            ) : (
              /* Crypto Payment Section */
              <div className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* QR Code */}
                  <div className="text-center">
                    <div className="bg-white p-4 rounded-xl shadow-md inline-block mb-4">
                      <img
                        src={CRYPTO_QR_CODE}
                        alt="USDT TRC20 QR Code"
                        data-testid="crypto-qr-code"
                        className="w-64 h-64 object-contain"
                      />
                    </div>
                    <p className="text-sm text-oar-text-secondary flex items-center justify-center gap-2">
                      <QrCode className="w-4 h-4" />
                      {t('donate.scanQR')}
                    </p>
                  </div>
                  
                  {/* Wallet Address */}
                  <div>
                    <h3 className="font-heading text-2xl font-semibold text-oar-text mb-4">
                      {t('donate.cryptoTitle')}
                    </h3>
                    
                    <div className="bg-oar-bg rounded-lg p-4 mb-4">
                      <p className="text-xs text-oar-text-secondary uppercase tracking-wider mb-2">
                        {t('donate.network')}
                      </p>
                      <p className="font-semibold text-oar-orange">USDT TRC20 (Tron)</p>
                    </div>
                    
                    <div className="bg-oar-bg rounded-lg p-4 mb-6">
                      <p className="text-xs text-oar-text-secondary uppercase tracking-wider mb-2">
                        {t('donate.walletAddress')}
                      </p>
                      <div className="flex items-center gap-2">
                        <code 
                          data-testid="crypto-wallet-address"
                          className="text-sm font-mono text-oar-text break-all flex-1"
                        >
                          {CRYPTO_WALLET}
                        </code>
                        <button
                          onClick={copyToClipboard}
                          data-testid="copy-wallet-btn"
                          className="flex-shrink-0 p-2 rounded-lg bg-white hover:bg-oar-blue hover:text-white text-oar-blue transition-all border border-oar-blue"
                        >
                          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </button>
                      </div>
                      {copied && (
                        <p className="text-sm text-green-600 mt-2">{t('donate.copied')}</p>
                      )}
                    </div>
                    
                    <div className="bg-oar-orange/10 border border-oar-orange/30 rounded-lg p-4">
                      <p className="text-sm text-oar-text">
                        <strong className="text-oar-orange">{t('donate.important')}:</strong>{' '}
                        {t('donate.cryptoWarning')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Thank You Message */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 text-oar-text-secondary">
              <Heart className="w-5 h-5 text-oar-red" />
              <p className="text-lg">{t('donate.thankYou')}</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DonatePage;
